import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Tooltip,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import StopIcon from "@mui/icons-material/Stop";

const QAPanel = ({
  onSendText,
  onInterrupt,
  transcripts,
  isAvatarSpeaking,
  isMicActive,
  onToggleMic,
}) => {
  const [inputText, setInputText] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [transcripts]);

  const handleSend = () => {
    if (!inputText.trim()) return;
    onSendText(inputText.trim());
    setInputText("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        borderRadius: "12px",
        backgroundColor: "#12162b",
        border: "1px solid rgba(124, 77, 255, 0.2)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Transcript area */}
      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          minHeight: "60px",
          maxHeight: "100px",
          overflowY: "auto",
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(124, 77, 255, 0.3)",
            borderRadius: "2px",
          },
        }}
      >
        {transcripts.length === 0 && (
          <Typography
            variant="body2"
            sx={{ color: "rgba(255,255,255,0.3)", textAlign: "center", mt: 4 }}
          >
            Ask a question using the mic or text input below...
          </Typography>
        )}
        {transcripts.map((t, i) => (
          <Box
            key={i}
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "flex-start",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: t.role === "user" ? "#64B5F6" : "#7C4DFF",
                fontWeight: 700,
                minWidth: "80px",
                flexShrink: 0,
              }}
            >
              {t.role === "user" ? "You" : "Avneesh"}:
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255,255,255,0.85)", lineHeight: 1.5 }}
            >
              {t.text}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Input area */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          p: 1.5,
          borderTop: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <Tooltip title={isMicActive ? "Mute microphone" : "Unmute microphone"}>
          <IconButton
            onClick={onToggleMic}
            sx={{
              color: isMicActive ? "#7C4DFF" : "#888",
              backgroundColor: isMicActive
                ? "rgba(124, 77, 255, 0.15)"
                : "transparent",
              "&:hover": { backgroundColor: "rgba(124, 77, 255, 0.2)" },
            }}
          >
            {isMicActive ? <MicIcon /> : <MicOffIcon />}
          </IconButton>
        </Tooltip>

        {isAvatarSpeaking && (
          <Tooltip title="Interrupt avatar">
            <IconButton
              onClick={onInterrupt}
              sx={{
                color: "#FF5252",
                "&:hover": { backgroundColor: "rgba(255, 82, 82, 0.15)" },
              }}
            >
              <StopIcon />
            </IconButton>
          </Tooltip>
        )}

        <TextField
          fullWidth
          size="small"
          placeholder="Type your question..."
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={{
            "& .MuiOutlinedInput-root": {
              color: "#fff",
              backgroundColor: "rgba(255,255,255,0.05)",
              borderRadius: "8px",
              "& fieldset": {
                borderColor: "rgba(255,255,255,0.1)",
              },
              "&:hover fieldset": {
                borderColor: "rgba(124, 77, 255, 0.4)",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#7C4DFF",
              },
            },
            "& .MuiOutlinedInput-input::placeholder": {
              color: "rgba(255,255,255,0.3)",
            },
          }}
        />

        <IconButton
          onClick={handleSend}
          disabled={!inputText.trim()}
          sx={{
            color: inputText.trim() ? "#7C4DFF" : "#555",
            "&:hover": { backgroundColor: "rgba(124, 77, 255, 0.15)" },
          }}
        >
          <SendIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default QAPanel;
